import { createContext, useContext, useMemo, useState } from "react";
import {
  CornerDownRight,
  GitBranch,
  ListChecks,
  MessageSquare,
  PanelRightClose,
  ShieldAlert,
  Sparkles,
  UserCheck,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CollapsibleSection } from "@/modules/simulator/sidebar/panel";
import { ProviderLogo } from "./sop-editor/components/ProviderLogo";
import { humanizeToolName } from "../lib/tool-name";
import { isStructuredInputs, outputNames } from "../lib/seg-node";
import {
  SchemaTabs,
  type JsonSchemaNode,
} from "./sop-editor/components/SchemaViewer";
import type { SEGNode } from "./seg-viewer/seg.types";
import { parseReferenceReplies } from "./seg-viewer/reference-replies";

/**
 * Read-only step inspector.
 *
 * Implementors need to *understand* a step without being able to change it, so this shows
 * every meaningful field in plain language — inputs, outputs, branches, escalation target,
 * response text — instead of the raw JSON editor used in developer mode.
 *
 * It reads top to bottom in the order the questions come: what is this step (the summary),
 * what does it do (its own sections), what happens after it, and only then how the
 * conversation gets here. Each part folds, and stays as the reader left it from step to step.
 */

/** Prose label for each node type, matching the workflow summary vocabulary. */
const TYPE_LABEL: Record<string, string> = {
  START: "Start",
  TOOL_CALL: "Action",
  CONDITION: "Check",
  LLM_DECIDE: "Decide",
  COLLECT_INPUT: "Ask the customer",
  RESPOND: "Reply",
  LLM_GENERATE: "Compose with AI",
  ESCALATE: "Hand over to a human",
  KB_LOOKUP: "Knowledge base lookup",
  JUMP: "Go to",
  WORKFLOW_LINK: "Linked protocol",
};

/** A labelled block; renders nothing when there is no value (keeps the panel quiet). */
export function Field({
  label,
  children,
}: {
  label: string;
  children?: React.ReactNode;
}) {
  if (children === undefined || children === null || children === "")
    return null;
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs leading-4 text-gray-800">{label}</p>
      <div className="text-[13px] leading-[18px] text-text-body">{children}</div>
    </div>
  );
}

export function Chips({ values }: { values: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((value) => (
        <Chip key={value}>{value}</Chip>
      ))}
    </div>
  );
}

/**
 * Rebuild a JSON schema from what the graph stored.
 *
 * The compiler flattens a tool's schema into `inputs.properties` + `required` and
 * `outputs` when it bakes the graph, so the schema viewer — which expects a real schema —
 * gets one back. Returns undefined when the graph carries no properties, which is how a
 * pre-schema workflow falls back to the plain required/produces chips.
 */
function schemaFromProperties(
  properties: Record<string, unknown> | undefined,
  required: string[] = [],
  anyOf: string[][] = [],
): JsonSchemaNode | undefined {
  if (!properties || Object.keys(properties).length === 0) return undefined;
  return {
    type: "object",
    properties: properties as Record<string, JsonSchemaNode>,
    ...(required.length > 0 && { required }),
    ...(anyOf.length > 0 && {
      anyOf: anyOf.map((group) => ({ required: group })),
    }),
  };
}

/** Which sections are folded. Owned by the viewer, so it carries from step to step and no further. */
export type PanelSections = Record<string, boolean>;
const SectionsContext = createContext<{
  sections: PanelSections;
  setSections: (update: (previous: PanelSections) => PanelSections) => void;
} | null>(null);

/** One folding section of the step panel. */
export function PanelSection({
  id,
  icon,
  title,
  defaultOpen = true,
  children,
}: {
  id: string;
  icon: LucideIcon;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const context = useContext(SectionsContext);
  const [own, setOwn] = useState<PanelSections>({});
  const sections = context?.sections ?? own;
  const setSections = context?.setSections ?? setOwn;
  return (
    <CollapsibleSection
      icon={icon}
      title={title}
      open={sections[id] ?? defaultOpen}
      onOpenChange={(open) => setSections((previous) => ({ ...previous, [id]: open }))}
    >
      {children}
    </CollapsibleSection>
  );
}

/** One short value: a field name, a type, a count. */
export const Chip = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={cn("rounded-md bg-gray-200 px-1.5 py-0.5 font-mono text-[11px] leading-4 text-gray-900", className)}>
    {children}
  </span>
);

/** The one-line bar on top of a drawer: what it shows, and its controls. */
export function DrawerBar({
  title,
  controls,
  onClose,
  closeLabel,
}: {
  title: React.ReactNode;
  /** Buttons beside close. */
  controls?: React.ReactNode;
  onClose: () => void;
  closeLabel: string;
}) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-1 border-b border-gray-500 pr-2 pl-4">
      <p className="flex min-w-0 flex-1 items-center gap-2 truncate text-sm font-medium text-text-heading">{title}</p>
      {controls}
      <Button variant="ghost" size="icon-sm" className="shrink-0 text-gray-900" onClick={onClose} aria-label={closeLabel}>
        <PanelRightClose className="size-4" />
      </Button>
    </div>
  );
}

/** A block of text the step sends or is told, set apart from the panel's own words. */
const Quoted = ({ children }: { children: React.ReactNode }) => (
  <p className="rounded-xl bg-gray-100 p-3 text-[13px] leading-[18px] whitespace-pre-wrap text-text-body">
    {children}
  </p>
);

/** One example turn, as the editor's Reply card draws it: a label over a thin coloured bar. */
const Turn = ({ who, bar, children }: { who: string; bar: string; children: string }) => (
  <div className={cn("flex min-w-0 flex-col gap-0.5 border-l-2 pl-2", bar)}>
    <span className="text-[11px] leading-4 font-medium text-gray-800">{who}</span>
    <p className="text-[13px] leading-[18px] break-words whitespace-pre-wrap text-text-body">{children}</p>
  </div>
);

/** A compiled Reply's sample exchanges, one card each; any other message stays plain text. */
function ReferenceReplies({ message }: { message: string }) {
  const replies = parseReferenceReplies(message);
  if (!replies) return <Quoted>{message}</Quoted>;
  return (
    <ol className="flex flex-col gap-1.5">
      {replies.map((example, index) => (
        <li key={index} className="flex flex-col gap-1.5 rounded-[8px] border border-gray-500 bg-gray-100 px-2.5 py-2">
          <span className="text-[11px] leading-4 font-medium tracking-wide text-gray-700 uppercase">
            {index === 0 ? "Example" : `Example ${index + 1}`}
          </span>
          {example.customer ? (
            <Turn who="Customer" bar="border-gray-500">
              {example.customer}
            </Turn>
          ) : null}
          <Turn who="Kim" bar="border-violet-200">
            {example.reply}
          </Turn>
        </li>
      ))}
    </ol>
  );
}

interface SegNodeInspectorProps {
  node: SEGNode | null;
  onClose: () => void;
  /** Maps node ids to labels so branch targets read as names, not ids. */
  labelById?: Map<string, string>;
  /** Makes branch targets links that open that step. */
  onNavigate?: (nodeId: string) => void;
  /** Header buttons beside close. */
  actions?: React.ReactNode;
  /** Cards under the step's summary: where it comes from, what it needs from the reader. */
  lead?: React.ReactNode;
  /** More of what happens after this step, shown with its own "then" and "on failure". */
  next?: React.ReactNode;
  /** Extra sections after the step's own. */
  children?: React.ReactNode;
  /** Which sections are folded, held by the viewer so it carries from step to step. */
  sections?: { value: PanelSections; onChange: (update: (previous: PanelSections) => PanelSections) => void };
  className?: string;
}

export function SegNodeInspector({
  node,
  onClose,
  labelById,
  onNavigate,
  actions,
  lead,
  next,
  children,
  sections,
  className,
}: SegNodeInspectorProps) {
  const sectionsContext = useMemo(
    () => (sections ? { sections: sections.value, setSections: sections.onChange } : null),
    [sections],
  );
  if (!node) return null;

  const inputs = node.inputs;
  const requiredInputs = isStructuredInputs(inputs)
    ? (inputs.required ?? [])
    : [];
  const anyOfGroups = isStructuredInputs(inputs) ? (inputs.anyOf ?? []) : [];
  const outputs = outputNames(node.outputs);
  const inputSchema = schemaFromProperties(
    isStructuredInputs(inputs) ? inputs.properties : undefined,
    requiredInputs,
    anyOfGroups,
  );
  const outputSchema = schemaFromProperties(
    node.outputs && !Array.isArray(node.outputs)
      ? (node.outputs as Record<string, unknown>)
      : undefined,
  );
  const pinnedInputs = Object.entries(
    (isStructuredInputs(inputs) ? inputs.static : undefined) ?? {},
  );
  const nameFor = (id: string) => {
    const name = labelById?.get(id) ?? id;
    return onNavigate ? (
      <button
        type="button"
        onClick={() => onNavigate(id)}
        className="text-left font-medium text-accent-purple underline-offset-2 hover:underline"
      >
        {name}
      </button>
    ) : (
      name
    );
  };

  const isTool = node.type === "TOOL_CALL";
  const title = isTool && node.tool ? humanizeToolName(node.tool) : node.label || node.id;
  const hasData =
    isTool &&
    Boolean(
      inputSchema ||
        outputSchema ||
        requiredInputs.length ||
        anyOfGroups.length ||
        outputs.length ||
        pinnedInputs.length,
    );
  const hasNext = Boolean((isTool && node.on_error) || next);
  const hasHandover = Boolean(
    node.attrs?.escalationType ||
      node.attrs?.agentName ||
      node.attrs?.agentEmail ||
      node.attrs?.to ||
      node.reason ||
      node.context_vars?.length,
  );

  return (
    <SectionsContext.Provider value={sectionsContext}>
    <aside
      className={cn(
        "flex w-[380px] shrink-0 flex-col overflow-hidden border-l border-gray-500 bg-white",
        className,
      )}
    >
      <DrawerBar
        title={
          <>
            {TYPE_LABEL[node.type] ?? node.type}
            {isTool && node.provider && (
              <span className="truncate font-normal text-gray-800 capitalize">· {node.provider}</span>
            )}
          </>
        }
        controls={actions}
        onClose={onClose}
        closeLabel="Close details"
      />

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {/* What this step is, before anything about it. */}
        <div className="flex shrink-0 flex-col gap-3 border-b border-gray-500 bg-gray-50 p-4">
          <div className="flex items-start gap-3">
            {isTool && (
              <ProviderLogo
                provider={node.provider ?? ""}
                size={28}
                borderRadius={8}
                padding={3}
                fallbackBg="var(--color-blue-600)"
                fallbackColor="white"
              />
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <h2 className="text-[15px] leading-5 font-semibold break-words text-text-heading">
                {title}
              </h2>
              {isTool && node.label && node.label !== title && (
                <p className="text-[13px] leading-[18px] break-words text-gray-900">
                  {node.label}
                </p>
              )}
            </div>
          </div>
          {isTool && node.riskLevel && !node.postProcessing && (
            <span
              className={cn(
                "flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] leading-4 font-medium",
                node.riskLevel === "high"
                  ? "bg-amber-50 text-amber-700"
                  : "bg-gray-200 text-gray-900",
              )}
            >
              <ShieldAlert className="size-3" />
              <span className="capitalize">{node.riskLevel}</span> risk
            </span>
          )}
          {lead}
        </div>

        {hasData && (
          <PanelSection id="data" icon={Wrench} title="Inputs and outputs">
            <div className="flex flex-col gap-4">
              {/* The tool's own schema when the graph carries it — the same Data Input /
                  Data Output read the editor gives, names and types together. Older
                  graphs recorded only which fields were required, so those still show
                  as chips. */}
              {inputSchema || outputSchema ? (
                <SchemaTabs
                  inputSchema={inputSchema}
                  outputSchema={outputSchema}
                />
              ) : (
                <>
                  {requiredInputs.length > 0 && (
                    <Field label="Required inputs">
                      <Chips values={requiredInputs} />
                    </Field>
                  )}
                  {anyOfGroups.length > 0 && (
                    <Field label="Needs at least one of">
                      <div className="flex flex-col gap-1.5">
                        {anyOfGroups.map((group, i) => (
                          <Chips key={i} values={group} />
                        ))}
                      </div>
                    </Field>
                  )}
                  {outputs.length > 0 && (
                    <Field label="Produces">
                      <Chips values={outputs} />
                    </Field>
                  )}
                </>
              )}
              {pinnedInputs.length > 0 && (
                <Field label="Values already set">
                  <ul className="flex flex-col rounded-xl border border-gray-500">
                    {pinnedInputs.map(([name, value], index) => (
                      <li
                        key={name}
                        className={cn(
                          "flex items-baseline justify-between gap-3 px-3 py-2",
                          index > 0 && "border-t border-gray-500",
                        )}
                      >
                        <span className="text-gray-800">{name}</span>
                        <span className="min-w-0 text-right break-words">
                          {String(value)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Field>
              )}
            </div>
          </PanelSection>
        )}

        {node.message && (
          <PanelSection
            id="message"
            icon={MessageSquare}
            title={node.type === "LLM_GENERATE" ? "Reference replies" : "Message sent"}
          >
            {node.type === "LLM_GENERATE" ? <ReferenceReplies message={node.message} /> : <Quoted>{node.message}</Quoted>}
          </PanelSection>
        )}

        {node.directive && (
          <PanelSection id="directive" icon={Sparkles} title="AI instruction">
            <Quoted>{node.directive}</Quoted>
          </PanelSection>
        )}

        {node.slots && node.slots.length > 0 && (
          <PanelSection
            id="slots"
            icon={ListChecks}
            title={`Details collected · ${node.slots.length}`}
          >
            <ul className="flex flex-col rounded-xl border border-gray-500">
              {node.slots.map((slot, index) => (
                <li
                  key={slot.name}
                  className={cn(
                    "flex flex-col gap-1 px-3 py-2.5",
                    index > 0 && "border-t border-gray-500",
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] leading-[18px] font-medium text-text-body">
                      {slot.name}
                    </span>
                    <Chip>{slot.type}</Chip>
                    {slot.required && (
                      <span className="text-[11px] leading-4 text-amber-700">
                        required
                      </span>
                    )}
                  </div>
                  <p className="text-xs leading-4 text-gray-800">{slot.prompt}</p>
                  {slot.options && slot.options.length > 0 && (
                    <Chips values={slot.options} />
                  )}
                </li>
              ))}
            </ul>
          </PanelSection>
        )}

        {node.guards && node.guards.length > 0 && (
          <PanelSection
            id="outcomes"
            icon={GitBranch}
            title={`Outcomes · ${node.guards.length}`}
          >
            <ol className="flex flex-col rounded-xl border border-gray-500">
              {node.guards.map((guard, i) => (
                <li
                  key={`${guard.condition}-${i}`}
                  className={cn(
                    "flex gap-2.5 px-3 py-2.5",
                    i > 0 && "border-t border-gray-500",
                  )}
                >
                  <span className="mt-px flex size-[18px] shrink-0 items-center justify-center rounded-full bg-gray-200 text-[11px] leading-4 text-gray-900 tabular-nums">
                    {i + 1}
                  </span>
                  <div className="flex min-w-0 flex-col gap-0.5 text-[13px] leading-[18px]">
                    <span className="break-words text-text-body">
                      {guard.condition === "always_true"
                        ? "Otherwise"
                        : guard.condition}
                    </span>
                    <span className="flex items-start gap-1 text-gray-800">
                      <CornerDownRight className="mt-0.5 size-3.5 shrink-0" />
                      {nameFor(guard.next)}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          </PanelSection>
        )}

        {node.type === "ESCALATE" && hasHandover && (
          <PanelSection id="handover" icon={UserCheck} title="Handover">
            <div className="flex flex-col gap-3">
              <Field label="Handover type">{node.attrs?.escalationType}</Field>
              <Field label="Assigned to">
                {node.attrs?.agentName ??
                  node.attrs?.agentEmail ??
                  node.attrs?.to}
              </Field>
              <Field label="Reason">{node.reason}</Field>
              {node.context_vars && node.context_vars.length > 0 && (
                <Field label="Context shared">
                  <Chips values={node.context_vars} />
                </Field>
              )}
            </div>
          </PanelSection>
        )}

        {hasNext && (
          <PanelSection id="next" icon={CornerDownRight} title="What happens next">
            <div className="flex flex-col gap-3">
              {next}
              {isTool && node.on_error && (
                <Field label="If it fails, goes to">
                  {nameFor(node.on_error)}
                </Field>
              )}
            </div>
          </PanelSection>
        )}

        {children}
      </div>
    </aside>
    </SectionsContext.Provider>
  );
}
